import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import { Ajv } from 'ajv';
import AjvKeywords from 'ajv-keywords';
import AjvFormats from 'ajv-formats';
import AjvErrors from 'ajv-errors';
export function ajvInstanceConstructor(ajvOptions) {
    const ajvInstance = new Ajv(ajvOptions);
    // CJS -> ESM workaround
    const ajvKeywords = AjvKeywords.default;
    const ajvFormats = AjvFormats.default;
    const ajvErrors = AjvErrors.default;
    // add instanceof keyword to the JSONSchema grammar. This allows to test native JS Date types.
    ajvKeywords(ajvInstance, ['instanceof']);
    // add formats like uuid which are now with version 7+ in separate package
    ajvFormats(ajvInstance);
    ajvErrors(ajvInstance);
    /**
     * there is a problem when removingAdditional Properties and using allOf and oneOf
     * the following code is a workaround to fix the problem
     * it redefine the allOf and oneOf keywords and does not remove additional properties for allOf and oneOf to keep
     * all the different options
     *
     * Example:
     *
     * options: allOf: [
     *  {properties: {type: 'A'}},
     *  {properties: {type: 'B'}},
     *  {properties: {type: 'C'}}
     * ]
     *
     * will not work when you validate {options: {type: 'C'}} without the fix
     * since it removes the options except 'A'
     *
     * Same applies for oneOf
     *
     * Taken from https://github.com/ajv-validator/ajv/issues/1231
     * */
    ajvInstance.removeKeyword('allOf');
    ajvInstance.addKeyword({
        keyword: 'allOf',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        macro: function (schema) {
            return merge({}, ...schema);
        },
        metaSchema: {
            type: 'array',
            items: [{ type: 'object' }],
        },
        errors: true,
    });
    ajvInstance.removeKeyword('oneOf');
    ajvInstance.addKeyword({
        keyword: 'oneOf',
        compile: function (schemas) {
            return function (data) {
                for (const schema of schemas) {
                    const validator = ajvInstance.compile(schema);
                    // use clone data to prevent data modify
                    const testData = cloneDeep(data);
                    const ret = validator(testData);
                    if (ret) {
                        // modify data for removeAddtional
                        validator(data);
                        return ret;
                    }
                }
                return false;
            };
        },
        modifying: true,
        metaSchema: {
            type: 'array',
            items: [{ type: 'object' }],
        },
        errors: false,
    });
    ajvInstance.addKeyword({
        keyword: 'arrayLength',
        type: 'array',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        validate: (schema, data) => {
            return data.length === schema;
        },
        errors: false,
    });
    ajvInstance.addKeyword({
        keyword: 'arrayLengthMultipleOf',
        type: 'array',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        validate: (schema, data) => {
            return data.length % schema === 0;
        },
        errors: false,
    });
    ajvInstance.addKeyword({
        keyword: 'arrayMinLength',
        type: 'array',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        validate: (schema, data) => {
            return data.length >= schema;
        },
        errors: false,
    });
    ajvInstance.addKeyword({
        keyword: 'arrayMaxLength',
        type: 'array',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        validate: (schema, data) => {
            return data.length <= schema;
        },
        errors: false,
    });
    return ajvInstance;
}
const ajv = ajvInstanceConstructor({
    allErrors: true,
    coerceTypes: false,
    removeAdditional: false, // in validate do not remove additional since we expect this to throw an error
    strict: false,
    useDefaults: true,
    discriminator: true,
});
export class StatusError extends Error {
    message;
    type;
    preventLog = false;
    statusCode;
    metadata;
    handled = true;
    constructor(message, statusCode = 500, metadata, customType) {
        super();
        this.name = this.constructor.name;
        this.message = message;
        this.metadata = metadata;
        this.statusCode = statusCode;
        this.type = customType || this.name;
    }
    getResponseBody() {
        return {
            type: this.type,
            message: this.message,
            statusCode: this.statusCode,
            metadata: this.metadata,
        };
    }
    getMetadata() {
        return this.metadata;
    }
}
export class ValidationError extends StatusError {
    handled = false;
    allErrors = [];
    importantErrors = [];
    body;
    constructor(
    // eslint-disable-next-line @rushstack/no-new-null
    allErrors, body, message, code = 422) {
        super(message || ValidationError._generateMessage(allErrors), code);
        this.allErrors = allErrors || [];
        this.importantErrors = ValidationError.filterNoisyOneOfErrors(this.allErrors);
        this.metadata = {
            translationKey: 'common.errorValidation',
            validationFailures: this.importantErrors,
        };
        this.body = body;
    }
    static _generateMessage(allErrors) {
        if (!allErrors || allErrors.length === 0) {
            return 'Validation failed';
        }
        // Get the first error message
        const firstError = allErrors[0];
        return firstError?.message || 'Validation failed';
    }
    /*
     * JSON schema errors for oneOf conditions are very noisy,
     * as they will contain all subschemas where the TYPE didn't match.
     * This method removes all errors where there was a mismatch between schema type and data type.
     */
    static filterNoisyOneOfErrors(errors) {
        const errorPrefixesToIgnore = [];
        errors.forEach((err) => {
            if (/oneOf\/\d+\/properties\/type\/enum$/.test(err.schemaPath)) {
                errorPrefixesToIgnore.push(err.schemaPath.replace(/properties\/type\/enum$/, ''));
            }
        });
        // filter out all JSON schema erros that relate to the paths which failed because of TYPE mismatch
        return errors.filter((err) => !errorPrefixesToIgnore.find((prefix) => err.schemaPath.startsWith(prefix)));
    }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validate = (body, schema) => {
    const valid = ajv.validate(schema, body); // modifies body by reference
    if (!valid)
        throw new ValidationError(ajv.errors, body);
    return body;
};
/**
 * Creates a helper function to enforce that an array includes all keys of a given object type `T`.
 * This function ensures at compile-time that no keys are missing or invalid.
 *
 * @template T - The object type whose keys must all be included.
 * @returns A function accepting exactly all keys of `T`, returning them as a strongly typed array.
 *
 * @example
 * interface User {
 *   id: number;
 *   name: string;
 *   email: string;
 * }
 *
 * const userKeys = hasAllKeysOf<User>()('id', 'name', 'email'); // OK
 * const invalid = hasAllKeysOf<User>()('id', 'name'); // Error: missing 'email'
 * const invalidExtra = hasAllKeysOf<User>()('id', 'name', 'email', 'extra'); // Error: invalid 'extra'
 */
export function hasAllKeysOf() {
    return (...keys) => keys;
}
/**
 * Helper function to enforce that a return value
 * matches an exact object shape — no extra keys allowed.
 *
 * This is useful for function return types, where
 * TypeScript normally permits extra properties.
 *
 * @param value - The object to validate
 * @returns The same object, if it matches exactly
 */
export function exactReturn(value) {
    return value;
}
//# sourceMappingURL=validate.js.map